import { useQuery } from '@tanstack/react-query'
import { useMemo, type PropsWithChildren } from 'react'
import { useLocation } from 'react-router-dom'

import {
  organizationsPermissionsUserListOptions,
  type PermissionsLocalized
} from 'src/api/main'
import { useSessionContext } from 'src/hooks/useSessionContext'
import { paths } from 'src/router/paths'

import { WorkspacePermissionsContext } from './WorkspacePermissionsContext'

/**
 * Reads the workspace id out of a `/workspaces/:id/...` pathname. Returns
 * `undefined` for the create flow and for any non-workspace route.
 */
export const resolveWorkspaceIdFromPath = (
  pathname: string
): number | undefined => {
  const base = `${paths.workspaces.root}/`
  if (!pathname.startsWith(base)) {
    return undefined
  }
  const rest = pathname.slice(base.length)
  const segment = rest.split('/')[0]
  if (!segment || segment === 'new' || segment === 'select') {
    return undefined
  }
  const id = Number(segment)

  return Number.isFinite(id) && id > 0 ? id : undefined
}

/** Hoisted so the memo below keeps a stable reference across error renders. */
const EMPTY_PERMISSIONS: PermissionsLocalized[] = []

type Props = PropsWithChildren<{
  /** Overrides route/session resolution — mainly for tests. */
  workspaceId?: number
}>

/**
 * Serves the signed-in user's effective workspace permissions to the whole
 * tree. The workspace is taken from the URL when the user is inside one, and
 * otherwise from the session's active workspace — so the sidebar is gated
 * correctly on account pages too, where there is no `:id` in the path.
 *
 * Mirrors `PermissionsProvider` in `ebs-platform-web`
 * (`packages/shared/src/context/PermissionsContext.tsx`): the query result is
 * exposed as `{ permissions, isLoading }` and consumers read it through
 * `useWorkspacePermissionsContext` / `useHasWorkspacePermissions`.
 */
export const WorkspacePermissionsProvider = ({
  children,
  workspaceId: workspaceIdProp
}: Props) => {
  const { pathname } = useLocation()
  const { activeWorkspaceId, isLoading: isLoadingSession } = useSessionContext()

  const workspaceId =
    workspaceIdProp ?? resolveWorkspaceIdFromPath(pathname) ?? activeWorkspaceId

  // Waiting on the session is not optional: firing before it settles means the
  // request goes out unauthenticated, comes back 401, and the error path below
  // resolves it to "no permissions" — a denial the user can only clear with a
  // reload.
  const enabled = workspaceId != null && !isLoadingSession

  // No `application` / `application_repository` filter: this is the
  // workspace-level list. App permissions arrive through the app's own
  // provider, and checks match on `code`, so a mixed list is harmless.
  const { data, isLoading, isError } = useQuery({
    ...organizationsPermissionsUserListOptions({
      query: { organization: workspaceId ?? 0 }
    }),
    enabled
  })

  // A failed request resolves to "no permissions", not to "still resolving".
  // Consumers hold a loader while `permissions` is `undefined`, so leaving it
  // unset after an error spins forever instead of showing the denial screen.
  const resolved = isError ? EMPTY_PERMISSIONS : data

  const value = useMemo(
    () => ({
      permissions: enabled ? resolved : undefined,
      isLoading: isLoadingSession || (enabled && isLoading),
      workspaceId
    }),
    [resolved, enabled, isLoading, isLoadingSession, workspaceId]
  )

  return (
    <WorkspacePermissionsContext.Provider value={value}>
      {children}
    </WorkspacePermissionsContext.Provider>
  )
}
